import { Client, cacheExchange, stringifyDocument } from '@urql/core';
import { multipartFetchExchange } from '@urql/exchange-multipart-fetch';
import { buildTaliAdminHeaders, resolveTaliViteApiEndpoints } from '@tali/platform-sdk';
import { resolveStoredDashboardLanguage } from '@/lib/i18n/dashboard-language';
import { REFRESH_ADMIN_TOKEN } from './operations/auth';

const TALI_API_ENDPOINTS = resolveTaliViteApiEndpoints(import.meta.env, 'http://localhost:3000/admin-api');
const TALI_ADMIN_API_URL = TALI_API_ENDPOINTS.admin;

// Request timeout in milliseconds (30 seconds)
const REQUEST_TIMEOUT = 30000;

/**
 * Auth token for bearer authentication (JWT from adminLogin response).
 *
 * The JWT contains a tenant channel claim used by the internal kernel bridge.
 *
 * Kept in memory/sessionStorage. Long-lived continuity is handled by an
 * HttpOnly refresh cookie, so the bearer access token is not persisted beyond
 * the current browser session.
 */
let _authToken: string | null = null;

const AUTH_SYNC_CHANNEL = 'kayn-dashboard-auth';
const AUTH_REFRESH_LOCK = 'kayn-dashboard-refresh-token';
let _authSyncChannel: BroadcastChannel | null = null;

const applyAuthToken = (token: string | null, broadcast: boolean) => {
  _authToken = token;
  if (token) {
    sessionStorage.setItem('kayn_auth_token', token);
    localStorage.removeItem('kayn_auth_token');
  } else {
    sessionStorage.removeItem('kayn_auth_token');
    localStorage.removeItem('kayn_auth_token');
  }
  _client = null;

  if (broadcast) {
    getAuthSyncChannel()?.postMessage({ type: 'token', token });
  }
};

const getAuthSyncChannel = (): BroadcastChannel | null => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (_authSyncChannel) return _authSyncChannel;

  _authSyncChannel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
  _authSyncChannel.addEventListener('message', (event: MessageEvent) => {
    const payload = event.data as { type?: string; token?: unknown } | null;
    if (payload?.type !== 'token') return;
    if (typeof payload.token === 'string' || payload.token === null) {
      applyAuthToken(payload.token, false);
    }
  });
  return _authSyncChannel;
};

// A dashboard view can issue several GraphQL requests at once. When the
// short-lived access token expires, every request can receive a 401 together.
// The refresh token is rotated on use, so those requests must share exactly one
// refresh attempt or they will race and replay an already-blacklisted cookie.
let _refreshPromise: Promise<boolean> | null = null;

// Channel token storage for multi-tenant support
// TALI tenant token used to scope every API request to one merchant.
let _channelToken: string | null = null;
// Tab-local override used only for platform impersonation. It must never be
// copied into shared localStorage or replace the merchant's normal selection.
let _ephemeralChannelToken: string | null = null;

interface ClientOptions {
  credentials?: RequestCredentials;
  includeAuthHeader?: boolean;
  includeChannelToken?: boolean;
}

/**
 * Vendure resolves translatable entity fields from the `languageCode` query
 * parameter. Keep the merchant's content language aligned with the dashboard
 * language instead of translating only the surrounding UI chrome.
 */
export const withAdminLanguageContext = (
  apiUrl: string,
  languageCode: 'ar' | 'en',
): string => {
  const url = new URL(apiUrl, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
  url.searchParams.set('languageCode', languageCode);
  if (/^https?:\/\//i.test(apiUrl)) return url.toString();
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Set the active channel token for all subsequent API requests.
 * This is critical for multi-tenant isolation - without it, superadmin users
 * would operate in the default channel context, causing cross-tenant data issues.
 */
export const setChannelToken = (token: string | null) => {
  _channelToken = token;
  // Persist channel token so it survives page navigations
  if (token) {
    localStorage.setItem('kayn_channel_token', token);
  } else {
    localStorage.removeItem('kayn_channel_token');
  }
  // Reset the client so the next request uses the new token
  _client = null;
};

/**
 * Override tenant scope for the lifetime of the current tab without mutating
 * the merchant's persisted channel selection.
 */
export const setEphemeralChannelToken = (token: string | null) => {
  _ephemeralChannelToken = token;
  _client = null;
};

/**
 * Set the auth token (JWT) for bearer authentication.
 * Called after adminLogin with the JWT from the response body.
 * Also used for signup auto-login.
 */
export const setAuthToken = (token: string | null) => {
  applyAuthToken(token, true);
};

/**
 * Get the current channel token
 */
export const getChannelToken = () => _ephemeralChannelToken ?? _channelToken;

const resolveStoredAuthToken = (): string | null => {
  if (_authToken) {
    return _authToken;
  }
  const storedAuthToken = sessionStorage.getItem('kayn_auth_token');
  if (localStorage.getItem('kayn_auth_token')) {
    localStorage.removeItem('kayn_auth_token');
  }
  if (storedAuthToken) {
    _authToken = storedAuthToken;
  }
  return storedAuthToken;
};

const resolveStoredChannelToken = (): string | null => {
  if (_ephemeralChannelToken) {
    return _ephemeralChannelToken;
  }
  if (_channelToken) {
    return _channelToken;
  }
  _channelToken = localStorage.getItem('kayn_channel_token');
  return _channelToken;
};

export const getTaliApiBaseUrl = (): string => TALI_API_ENDPOINTS.base;
export const getTaliShopApiUrl = (): string => TALI_API_ENDPOINTS.shop;

/** Authenticated REST headers for TALI endpoints outside GraphQL. */
export const getTaliRestHeaders = (contentType?: string): Record<string, string> =>
  buildTaliAdminHeaders({
    accessToken: resolveStoredAuthToken(),
    tenantToken: resolveStoredChannelToken(),
    contentType,
  });

/**
 * Custom fetch with timeout.
 *
 * Auth tokens are managed explicitly via setAuthToken() (JWT from login response).
 * We do not capture the kernel session response here because:
 * 1. The TenantResolverMiddleware needs a JWT (with channelId claim) in the
 *    Authorization header for tenant resolution.
 * 2. Kernel session tokens returned in response headers do not carry
 *    channelId claims and would break tenant resolution if used as bearer tokens.
 * 3. The middleware bridges the TALI JWT to the kernel session internally.
 */
const fetchWithTimeout = (url: RequestInfo | URL, options: RequestInit = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  return fetch(url, {
    ...options,
    signal: controller.signal,
  }).finally(() => clearTimeout(timeoutId));
};

const requestAdminAccessTokenRefresh = async (): Promise<boolean> => {
  try {
    const response = await fetchWithTimeout(TALI_ADMIN_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        query: stringifyDocument(REFRESH_ADMIN_TOKEN),
        variables: {},
      }),
    });

    if (!response.ok) {
      return false;
    }

    const payload = await response.json() as {
      data?: { refreshAdminToken?: { token?: string | null } | null };
      errors?: unknown[];
    };
    const token = payload.data?.refreshAdminToken?.token;
    if (payload.errors?.length || !token) {
      return false;
    }

    setAuthToken(token);
    return true;
  } catch (error) {
    console.warn('Failed to refresh admin access token from refresh cookie:', error);
    return false;
  }
};

const requestAdminAccessTokenRefreshAcrossTabs = async (): Promise<boolean> => {
  // Subscribe before waiting for the lock so this tab receives the token
  // produced by the tab currently rotating the HttpOnly refresh cookie.
  getAuthSyncChannel();
  const tokenBeforeLock = resolveStoredAuthToken();

  const refreshIfStillNeeded = async () => {
    const currentToken = resolveStoredAuthToken();
    if (currentToken && currentToken !== tokenBeforeLock) {
      return true;
    }
    return requestAdminAccessTokenRefresh();
  };

  // Refresh cookies rotate on every use. Web Locks serialize that rotation
  // across same-origin tabs; BroadcastChannel delivers the resulting access
  // token to the waiting tabs without persisting it in localStorage.
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(AUTH_REFRESH_LOCK, refreshIfStillNeeded);
  }

  return refreshIfStillNeeded();
};

/**
 * Refresh the short-lived dashboard JWT from the protected cookie.
 *
 * This is single-flight because the backend rotates and blacklists refresh
 * tokens. Concurrent callers must all await the same rotation result.
 */
export const refreshAdminAccessToken = (): Promise<boolean> => {
  if (_refreshPromise) {
    return _refreshPromise;
  }

  const refreshAttempt = requestAdminAccessTokenRefreshAcrossTabs().finally(() => {
    if (_refreshPromise === refreshAttempt) {
      _refreshPromise = null;
    }
  });
  _refreshPromise = refreshAttempt;
  return refreshAttempt;
};

const fetchWithAuthRefresh = async (
  url: RequestInfo | URL,
  options: RequestInit = {},
): Promise<Response> => {
  const response = await fetchWithTimeout(url, options);
  const requestHeaders = new Headers(options.headers);

  // Only retry authenticated requests rejected before GraphQL execution.
  // A 403 is intentionally not refreshed: it can represent a valid merchant
  // session which lacks permission for the requested operation.
  if (response.status !== 401 || !requestHeaders.has('Authorization')) {
    return response;
  }

  const refreshed = await refreshAdminAccessToken();
  if (!refreshed || !_authToken) {
    return response;
  }

  const retryHeaders = new Headers(options.headers);
  retryHeaders.set('Authorization', `Bearer ${_authToken}`);
  return fetchWithTimeout(url, {
    ...options,
    headers: retryHeaders,
  });
};

export const createClient = (
  authToken?: string | null,
  channelToken?: string | null,
  options: ClientOptions = {},
) => {
  const includeAuthHeader = options.includeAuthHeader ?? true;
  const includeChannelToken = options.includeChannelToken ?? true;
  const resolvedAuthToken = includeAuthHeader ? (authToken ?? _authToken) : null;
  const resolvedChannelToken = includeChannelToken
    ? (channelToken ?? _ephemeralChannelToken ?? _channelToken)
    : null;
  const credentials = options.credentials ?? (resolvedAuthToken ? 'omit' : 'include');

  return new Client({
    url: withAdminLanguageContext(TALI_ADMIN_API_URL, resolveStoredDashboardLanguage()),
    exchanges: [cacheExchange, multipartFetchExchange],
    fetch: includeAuthHeader ? fetchWithAuthRefresh : fetchWithTimeout,
    fetchOptions: () => {
      const headers = buildTaliAdminHeaders({
        accessToken: resolvedAuthToken,
        tenantToken: resolvedChannelToken,
      });
      // Standard authenticated API traffic omits cookies because bearer auth
      // is the source of truth. Some bootstrap flows override this:
      // - login / 2FA verification omit cookies and all stale auth state
      // - logout includes cookies so the backend can clear the HttpOnly session
      return {
        headers,
        credentials,
      };
    },
  });
};

/**
 * Client for public auth bootstrap mutations such as adminLogin and
 * verifyTwoFactorLogin. It deliberately avoids stored JWT/channel headers while
 * allowing credentials so the backend can set/rotate the HttpOnly refresh cookie.
 */
export const getPublicAuthClient = () =>
  createClient(null, null, {
    credentials: 'include',
    includeAuthHeader: false,
    includeChannelToken: false,
  });

export const getRefreshClient = () =>
  createClient(null, null, {
    credentials: 'include',
    includeAuthHeader: false,
    includeChannelToken: false,
  });

/**
 * Channel switching rotates both the access JWT returned in the GraphQL body
 * and the HttpOnly refresh cookie returned in Set-Cookie. Credentials must be
 * included so the browser accepts that cookie; otherwise the next refresh
 * silently restores the previous channel while the UI still shows the newly
 * selected store.
 */
export const getChannelSwitchClient = () =>
  createClient(resolveStoredAuthToken(), resolveStoredChannelToken(), {
    credentials: 'include',
  });

/**
 * Logout needs credentials included so the browser accepts Set-Cookie
 * headers that clear the HttpOnly kernel session cookie.
 */
export const getLogoutClient = () =>
  createClient(resolveStoredAuthToken(), resolveStoredChannelToken(), {
    credentials: 'include',
  });

// Lazy initialize client only on client-side
let _client: Client | null = null;

export const getClient = () => {
  if (!_client) {
    _client = createClient(resolveStoredAuthToken(), resolveStoredChannelToken());
  }
  return _client;
};

// Shop API client for public operations (signup, verification)
// These endpoints don't require authentication
const SHOP_API_URL = TALI_API_ENDPOINTS.shop;

const createShopClient = () => {
  return new Client({
    url: SHOP_API_URL,
    exchanges: [cacheExchange, multipartFetchExchange],
    fetch: fetchWithTimeout,
    fetchOptions: () => ({
      headers: {},
      credentials: 'include' as RequestCredentials,
    }),
  });
};

let _shopClient: Client | null = null;

export const getShopClient = () => {
  if (!_shopClient) {
    _shopClient = createShopClient();
  }
  return _shopClient;
};
